/**
 * IncidentFormSections - Reusable form sections for incident reporting
 */

import React from 'react';
import { Info } from 'lucide-react';
import Dropdown from '../../../../common/Dropdown';

// Basic Information Section
export const BasicInfoSection = ({ formData, handleInputChange, incidentTypes, locations, errors }) => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
      Basic Information
      <Info className="h-4 w-4 ml-2 text-gray-400" />
    </h2>
    
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type <span className="text-red-500">*</span>
        </label>
        <Dropdown
          value={formData.type}
          onChange={(val) => handleInputChange('type', val)}
          options={incidentTypes}
          width="w-full"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => handleInputChange('date', e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Time <span className="text-red-500">*</span>
        </label>
        <input
          type="time"
          value={formData.time}
          onChange={(e) => handleInputChange('time', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Daylight Conditions
        </label>
        <Dropdown
          value={formData.daylight}
          onChange={(val) => handleInputChange('daylight', val)}
          options={[
            { value: 'daylight', label: 'Daylight' },
            { value: 'dawn_dusk', label: 'Dawn/Dusk' },
            { value: 'darkness', label: 'Darkness' }
          ]}
          width="w-full"
        />
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Location <span className="text-red-500">*</span>
        </label>
        <Dropdown
          value={formData.location}
          onChange={(val) => handleInputChange('location', val)}
          options={locations}
          placeholder="Select location..."
          width="w-full"
          error={!!errors.location}
        />
        {errors.location && (
          <p className="mt-1 text-sm text-red-600">{errors.location}</p>
        )}
      </div>
      
      {formData.location === 'other' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Specify Location <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.locationOther}
            onChange={(e) => handleInputChange('locationOther', e.target.value)}
            placeholder="Enter specific location"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
              errors.locationOther ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.locationOther && (
            <p className="mt-1 text-sm text-red-600">{errors.locationOther}</p>
          )}
        </div>
      )}
    </div>
  </div>
);


// Severity & Impact Section
export const SeverityImpactSection = ({ formData, handleInputChange, errors }) => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-lg font-medium text-gray-900 mb-4">
      Severity & Impact
    </h2>
    
    <div className="space-y-4">
      {/* Severity Checkboxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
          <input
            type="checkbox"
            checked={formData.injuries}
            onChange={(e) => handleInputChange('injuries', e.target.checked)}
            className="h-4 w-4 mt-0.5 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <span className="text-sm font-medium text-gray-700">Injuries Occurred</span>
            <p className="text-xs text-gray-500">Check if anyone was injured</p>
          </div>
        </label>
        
        <label className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
          <input
            type="checkbox"
            checked={formData.damage}
            onChange={(e) => handleInputChange('damage', e.target.checked)}
            className="h-4 w-4 mt-0.5 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <span className="text-sm font-medium text-gray-700">Damage to Vessel/Equipment</span>
            <p className="text-xs text-gray-500">Check if damage occurred</p>
          </div>
        </label>
        
        <label className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
          <input
            type="checkbox"
            checked={formData.pollutionRisk}
            onChange={(e) => handleInputChange('pollutionRisk', e.target.checked)}
            className="h-4 w-4 mt-0.5 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
          />
          <div className="flex-1">
            <span className="text-sm font-medium text-gray-700">Pollution Risk/Occurred</span>
            <p className="text-xs text-gray-500">Environmental impact</p>
          </div>
        </label>
      </div>

      {/* Conditional Details */}
      {formData.injuries && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Injury Details <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.injuryDetails}
            onChange={(e) => handleInputChange('injuryDetails', e.target.value)}
            rows={2}
            placeholder="Describe injuries and treatment provided..."
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
              errors.injuryDetails ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.injuryDetails && (
            <p className="mt-1 text-sm text-red-600">{errors.injuryDetails}</p>
          )}
        </div>
      )}

      {formData.damage && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Damage Details <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.damageDetails}
            onChange={(e) => handleInputChange('damageDetails', e.target.value)}
            rows={2}
            placeholder="Describe damage and estimated repair requirements..."
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
              errors.damageDetails ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.damageDetails && (
            <p className="mt-1 text-sm text-red-600">{errors.damageDetails}</p>
          )}
        </div>
      )}

      {/* Medical Response Indicators */}
      {formData.injuries && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Medical Response Required</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-yellow-50 rounded-lg">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.medicalEvacuation}
                onChange={(e) => handleInputChange('medicalEvacuation', e.target.checked)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Medical evacuation required</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hospitalization}
                onChange={(e) => handleInputChange('hospitalization', e.target.checked)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Hospitalization required</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.ambulanceCalled}
                onChange={(e) => handleInputChange('ambulanceCalled', e.target.checked)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Ambulance called</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.coastguardMedicalAdvice}
                onChange={(e) => handleInputChange('coastguardMedicalAdvice', e.target.checked)}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Coastguard medical advice sought</span>
            </label>
          </div>
        </div>
      )}

      {/* Severity Indicators - Better aligned with reporting requirements */}
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Incident Severity Indicators</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
          {/* Critical indicators for MAIB reporting */}
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.vesselLost}
              onChange={(e) => handleInputChange('vesselLost', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Vessel lost or constructive total loss</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.vesselAbandoned}
              onChange={(e) => handleInputChange('vesselAbandoned', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Vessel abandoned</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.grounding}
              onChange={(e) => handleInputChange('grounding', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Grounding occurred</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.collision}
              onChange={(e) => handleInputChange('collision', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Collision (with vessel/object)</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.fire}
              onChange={(e) => handleInputChange('fire', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Fire or explosion</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.flooding}
              onChange={(e) => handleInputChange('flooding', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Flooding or taking on water</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.navigationSafetyCompromised}
              onChange={(e) => handleInputChange('navigationSafetyCompromised', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Navigation safety compromised</span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.emergencyServicesContacted}
              onChange={(e) => handleInputChange('emergencyServicesContacted', e.target.checked)}
              className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Emergency services contacted</span>
          </label>
        </div>
      </div>
    </div>
  </div>
);
